// usePinned — generic, tenant-scoped pinned-IDs hook.
//
// Surfaces: opportunity (Horizon, /terminal/closing-soon, /awards), intel
// (future), vendor / agency / award browsers (future). All consumers share
// this one hook — Iron Rule #11 (no bespoke per-page pin logic).
//
// Storage: IndexedDB via `openPinnedStore`. Persists across reload, scopes
// per tenant + entityKey so multiple tenants in the same browser do not
// bleed.
//
// Cross-tab sync: when a pin changes in tab A, tab B's pinned set updates
// without a reload. We use a `BroadcastChannel` per (tenant, entityKey) when
// available, falling back to a `storage` event on `localStorage` (we only
// write a tiny "rev" key to localStorage to broadcast — the source of truth
// stays in IDB). BroadcastChannel is universal in browsers since 2022 (all
// major engines), but the localStorage fallback keeps us safe in Safari
// private-mode and any embedded WebView that disables BroadcastChannel.
//
// API:
//   const { pinnedIds, pinnedSet, isPinned, toggle, count, ready } =
//     usePinned({ tenantId, entityKey: 'opp' });
//
//   pinnedIds   — string[] newest-pin first
//   pinnedSet   — Set<string> for O(1) membership lookups in render
//   isPinned(id) — convenience boolean
//   toggle(id)  — pin if missing, unpin if present; persists, broadcasts
//   count       — pinnedIds.length
//   ready       — false until the initial IDB load resolves

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type PinnedEntry,
  type PinnedStore,
  openPinnedStore,
  pinsStoreName,
} from './idb/pinned-store.js';

export interface UsePinnedOptions {
  /** Tenant the pin list is scoped to. Pass `null` when not yet authed. */
  tenantId: string | null;
  /** Entity kind. Short lowercase identifier, e.g. "opp", "intel", "vendor". */
  entityKey: string;
  /** Test override — fake-indexeddb factory. */
  idbFactory?: IDBFactory;
  /** Test override — pretend BroadcastChannel is unavailable. */
  disableBroadcastChannel?: boolean;
}

export interface UsePinnedResult {
  pinnedIds: string[];
  pinnedSet: ReadonlySet<string>;
  isPinned: (id: string) => boolean;
  toggle: (id: string) => void;
  count: number;
  ready: boolean;
}

// Tiny per-storage-name `rev` key. Bumping it signals other tabs to re-read.
function revKey(storeName: string): string {
  return `${storeName}:rev`;
}

export function usePinned(opts: UsePinnedOptions): UsePinnedResult {
  const { tenantId, entityKey, idbFactory, disableBroadcastChannel } = opts;
  const [entries, setEntries] = useState<PinnedEntry[]>([]);
  const [ready, setReady] = useState(false);
  const storeRef = useRef<PinnedStore | null>(null);

  const storeName = useMemo(
    () => (tenantId ? pinsStoreName(tenantId, entityKey) : null),
    [tenantId, entityKey],
  );

  // --- Cold load on (tenant, entityKey) change. ---
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setEntries([]);
    storeRef.current?.close();
    storeRef.current = null;

    if (!tenantId) {
      setReady(true);
      return () => {
        cancelled = true;
      };
    }

    void (async () => {
      try {
        const store = await openPinnedStore({
          tenantId,
          entityKey,
          ...(idbFactory ? { factory: idbFactory } : {}),
        });
        if (cancelled) {
          store.close();
          return;
        }
        const list = await store.list();
        if (cancelled) {
          store.close();
          return;
        }
        storeRef.current = store;
        setEntries(list);
        setReady(true);
      } catch {
        // IDB unavailable (private mode w/o storage, SSR, hostile env). Hook
        // is no-op — render proceeds without any pinned state.
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
      storeRef.current?.close();
      storeRef.current = null;
    };
  }, [tenantId, entityKey, idbFactory]);

  // --- Cross-tab sync. ---
  useEffect(() => {
    if (!storeName || !tenantId) return;

    const refresh = () => {
      const s = storeRef.current;
      if (!s) return;
      void s
        .list()
        .then((list) => setEntries(list))
        .catch(() => {
          /* idb error: keep previous state */
        });
    };

    const useBC = !disableBroadcastChannel && typeof BroadcastChannel !== 'undefined';

    let bc: BroadcastChannel | null = null;
    if (useBC) {
      bc = new BroadcastChannel(storeName);
      bc.onmessage = (ev) => {
        if (ev.data === 'change') refresh();
      };
    }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === revKey(storeName)) refresh();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
    }

    return () => {
      bc?.close();
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    };
  }, [storeName, tenantId, disableBroadcastChannel]);

  // --- toggle ---
  const broadcastChange = useCallback(() => {
    if (!storeName) return;
    // BroadcastChannel (preferred, all-tab fanout incl. same-origin).
    if (!disableBroadcastChannel && typeof BroadcastChannel !== 'undefined') {
      try {
        const bc = new BroadcastChannel(storeName);
        bc.postMessage('change');
        bc.close();
      } catch {
        /* fall through */
      }
    }
    // localStorage `storage` event — fires only on OTHER tabs, which is
    // exactly what we want. Writing to our own tab does not re-trigger
    // our `storage` listener, so we avoid feedback loops.
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(revKey(storeName), String(Date.now()));
      } catch {
        /* quota / private-mode — best-effort */
      }
    }
  }, [storeName, disableBroadcastChannel]);

  const toggle = useCallback(
    (id: string) => {
      const store = storeRef.current;
      if (!store) return;
      setEntries((prev) => {
        const has = prev.some((e) => e.id === id);
        if (has) {
          // Optimistic remove.
          void store
            .remove(id)
            .then(broadcastChange)
            .catch(() => {
              /* if it fails, refresh on next mount picks up the truth */
            });
          return prev.filter((e) => e.id !== id);
        }
        // Optimistic add — pinned_at = now, newest-first.
        const entry: PinnedEntry = { id, pinned_at: Date.now() };
        void store
          .add(id)
          .then(broadcastChange)
          .catch(() => {
            /* on failure, future refresh corrects */
          });
        return [entry, ...prev];
      });
    },
    [broadcastChange],
  );

  const pinnedIds = useMemo(() => entries.map((e) => e.id), [entries]);
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const isPinned = useCallback((id: string) => pinnedSet.has(id), [pinnedSet]);

  return {
    pinnedIds,
    pinnedSet,
    isPinned,
    toggle,
    count: pinnedIds.length,
    ready,
  };
}

/**
 * Stable utility: sort a row array so pinned ids come first, in pin-order
 * (newest pin first), and unpinned rows retain their original order.
 *
 * Pure — exported so non-React consumers (tests, future server-rendered
 * lists) can reuse the same ordering.
 */
export function sortPinnedFirst<R>(
  rows: R[],
  pinnedIds: string[],
  rowKey: (row: R) => string,
): R[] {
  if (pinnedIds.length === 0) return rows;
  const order = new Map<string, number>();
  pinnedIds.forEach((id, i) => order.set(id, i));
  const pinned: R[] = [];
  const rest: R[] = [];
  for (const r of rows) {
    if (order.has(rowKey(r))) pinned.push(r);
    else rest.push(r);
  }
  pinned.sort((a, b) => (order.get(rowKey(a)) ?? 0) - (order.get(rowKey(b)) ?? 0));
  return [...pinned, ...rest];
}
